// Vocabolario e validazione dei moduli che scrivono all'Osservatorio.
//
// Sono due, e passano dalla stessa catena: il modulo di /contatti
// (`modulo: "contatti"`) e il briefing di /accoglienza
// (`modulo: "accoglienza"`). Cambia la forma dei campi, non il resto:
// stessa validazione, stesso endpoint, stessi limiti di frequenza,
// stesso canale di consegna, stesse due caselle di consenso.
//
// Sta in `lib/` e non accanto ai form per una ragione precisa: il route
// handler deve leggere le stesse liste che i form disegnano (i tipi di
// organizzazione, gli slug dei servizi, gli obiettivi del briefing, i
// limiti dei campi), e una `export const` di un file marcato "use client"
// letta da un Server Component non è il suo valore, è il riferimento al
// modulo client, che al primo uso tira
// `Attempted to call X() from the server`. Nessuno dei due strumenti lo
// prende: né `tsc --noEmit` né `npm run build`.
//
// La validazione è qui e non nel route handler perché il file non
// importa niente di server-only: i form possono usarla per anticipare gli
// errori senza andare in rete, e il server la riesegue comunque. Quella
// del client è comodità, quella del server è l'unica che conta.

import { INFORMATIVA_VERSIONE } from "@/lib/legale";
import { SERVICES } from "@/lib/services";

/** Quale dei due moduli ha prodotto la richiesta. Viaggia nel payload e
 *  arriva fino a chi la riceve: le due domande sono diverse, e chi
 *  risponde deve sapere se ha davanti una richiesta di informazioni o
 *  un'impresa che ha raccontato il proprio problema. */
export const MODULI = ["contatti", "accoglienza"] as const;

export type Modulo = (typeof MODULI)[number];

/** I sei profili del passaggio «02 · Chi sei» di /contatti. Gli `id`
 *  viaggiano nel payload, quindi cambiarli invalida le richieste già in
 *  coda da qualche parte.
 *
 *  Il modello del 19/10/2026 (`uploads/contatti.html`, `data-profilo`)
 *  ha gli stessi sei profili con le stesse parole, nello stesso ordine:
 *  il vocabolario non cambia, cambia solo la veste (un'icona per profilo,
 *  che sta nel modulo e non qui perché il server non la legge). */
export const TIPI_ORGANIZZAZIONE = [
  { id: "pmi", label: "PMI" },
  { id: "multinazionale", label: "Multinazionale" },
  { id: "istituzione", label: "Istituzione" },
  { id: "professionista", label: "Professionista" },
  { id: "ricercatore", label: "Ricercatore" },
  { id: "associazione", label: "Associazione / Media" },
] as const;

export type TipoOrganizzazione = (typeof TIPI_ORGANIZZAZIONE)[number]["id"];

/** Il nome del campo esca del modulo di /contatti. Fino al 18/10/2026 era
 *  `sito`, ma il modello del 19/10 chiede un campo «Sito web» vero, che
 *  con quel nome sarebbe stato scambiato per la trappola e ogni richiesta
 *  con un sito compilato sarebbe stata buttata in silenzio. Il nome nuovo
 *  è uno che una persona non compila (nessuno ha più un fax da dare a un
 *  modulo web) e che un bot riempie volentieri.
 *
 *  Dal 25/10/2026 anche il briefing di /accoglienza ha un campo «Sito
 *  web» vero (`uploads/accoglienza-riferimento.html`, passo 1), e la sua
 *  esca si chiama `fax` come qui. Fino ad allora la mandava come `sito`. */
export const CAMPO_ESCA = "fax";

/** Il campo esca di una richiesta. Era diverso per modulo finché il
 *  briefing non ha avuto il suo campo sito (25/10/2026): ora è lo stesso,
 *  e la funzione resta perché il route handler la chiama così. Una pagina
 *  del briefing servita prima di quel giorno manda ancora l'esca come
 *  `sito`: la validazione la legge come un sito vero, che vuoto non dice
 *  niente e compilato da un bot non supera il controllo dell'indirizzo. */
export function nomeEsca(modulo: unknown): string {
  void modulo;
  return CAMPO_ESCA;
}

/** Gli obiettivi del briefing di /accoglienza. Non sono i servizi del
 *  catalogo, e non vanno confusi con `interessi`: là si sceglie un
 *  servizio che esiste, qui si dice che cosa si vuole ottenere, che è la
 *  domanda giusta da fare a chi non sa ancora quale servizio gli serve. */
export const OBIETTIVI = [
  { id: "crescere", label: "Crescere in un mercato estero" },
  { id: "proteggere", label: "Proteggere l'azienda da rischi" },
  { id: "valutare", label: "Valutare un partner o un fornitore" },
  { id: "sanzioni", label: "Conformità normativa e sanzioni" },
  { id: "filiera", label: "Sicurezza della filiera" },
  { id: "strategia", label: "Pianificazione strategica" },
  { id: "materie", label: "Reperire materie prime" },
  { id: "altro", label: "Altro (lo scrivo sotto)" },
] as const;

export type ObiettivoId = (typeof OBIETTIVI)[number]["id"];

const TIPI_VALIDI: readonly string[] = TIPI_ORGANIZZAZIONE.map((t) => t.id);
const SLUG_VALIDI: readonly string[] = SERVICES.map((s) => s.slug);
const OBIETTIVI_VALIDI: readonly string[] = OBIETTIVI.map((o) => o.id);

/** Misure dei campi. `max` serve due volte: come `maxLength` sugli input
 *  (che ferma la digitazione) e come rifiuto sul server (che ferma chi
 *  gli input non li usa). `email` sta a 254 perché è il massimo di una
 *  casella secondo la RFC 5321, non un numero scelto a occhio. */
export const LIMITI = {
  nome: { min: 2, max: 120 },
  ruolo: { max: 120 },
  email: { max: 254 },
  azienda: { max: 160 },
  partitaIva: { max: 20 },
  settore: { max: 120 },
  telefono: { max: 32 },
  sito: { max: 200 },
  messaggio: { min: 20, max: 4000 },
  interessi: { max: SLUG_VALIDI.length },
  obiettivi: { max: OBIETTIVI_VALIDI.length },
} as const;

/** I campi che possono portare un errore. La chiave è la stessa nel
 *  payload, nella risposta del server e nello stato del form: così la
 *  mappa degli errori si passa da una parte all'altra senza tradurla.
 *
 *  L'elenco è l'unione dei due moduli: ognuno usa il suo sottoinsieme, e
 *  un campo che non gli appartiene non viene né letto né segnalato. */
export type CampoContatto =
  | "tipo"
  | "nome"
  | "ruolo"
  | "email"
  | "azienda"
  | "partitaIva"
  | "settore"
  | "telefono"
  | "sito"
  | "interessi"
  | "obiettivi"
  | "messaggio"
  | "presaVisione";

export type ErroriContatto = Partial<Record<CampoContatto, string>>;

/** La richiesta normalizzata, cioè quello che esce dalla validazione e
 *  che il canale di consegna riceve. I campi facoltativi sono `null` e
 *  mai stringa vuota: a valle nessuno deve chiedersi se `""` significa
 *  "non compilato" oppure "compilato male".
 *
 *  La forma è **una sola** per tutti e due i moduli, e i campi che il
 *  modulo di partenza non chiede arrivano vuoti. L'alternativa, due tipi
 *  e due payload, obbligherebbe chi riceve a due integrazioni per una
 *  casella di posta sola. */
export type RichiestaContatto = {
  modulo: Modulo;
  tipo: TipoOrganizzazione | null;
  nome: string;
  /** Ruolo in azienda. Solo /accoglienza, facoltativo. */
  ruolo: string | null;
  email: string;
  /** Su /contatti è facoltativa, sul briefing di /accoglienza no: là la
   *  domanda è rivolta a un'impresa, e senza ragione sociale non si sa
   *  chi si sta per richiamare. */
  azienda: string | null;
  /** Solo /accoglienza, obbligatoria: è quello che rende l'impresa
   *  identificabile prima ancora di sentirsi al telefono. */
  partitaIva: string | null;
  /** Solo /accoglienza, facoltativo. */
  settore: string | null;
  /** Solo /accoglienza, facoltativo. */
  telefono: string | null;
  /** Sito web dell'impresa o dell'ente. Solo /contatti, facoltativo, e
   *  sempre con lo schema davanti (`https://` se chi scrive non l'ha
   *  messo): a valle è un collegamento da aprire, non un testo. */
  sito: string | null;
  /** Servizi del catalogo, solo /contatti. */
  interessi: string[];
  /** Obiettivi del briefing, solo /accoglienza. */
  obiettivi: string[];
  messaggio: string;
  /** Versione dell'informativa che chi scrive ha dichiarato di aver
   *  letto. La scrive il server, non il browser: il client dichiara solo
   *  di aver preso visione, e quale testo avesse davanti lo sa chi lo ha
   *  servito. Un client che scegliesse da sé la versione potrebbe
   *  dichiarare presa visione di un'informativa mai pubblicata. */
  informativaVersione: string;
  /** Consenso **facoltativo** a ricevere gli aggiornamenti editoriali.
   *  Non è la base giuridica della risposta alla richiesta, che è
   *  precontrattuale: è una finalità ulteriore e separata, e per questo
   *  parte spento e si può negare senza perdere niente. */
  consensoAggiornamenti: boolean;
};

export type EsitoValidazione =
  | { ok: true; dati: RichiestaContatto }
  | { ok: false; errori: ErroriContatto };

const TABULAZIONE = "\t";
const A_CAPO = "\n";

/**
 * Toglie i caratteri di controllo, tenendo solo quelli elencati in
 * `tieni`. In un campo che finirà in un'intestazione di posta o in un
 * JSON verso un servizio terzo sono il vettore classico dell'injection,
 * e non c'è nessun caso in cui uno li scriva davvero in un form.
 *
 * È un ciclo e non una regex di proposito: la classe si scriverebbe con
 * degli escape esadecimali che, passando da un editor all'altro, si
 * trasformano nei caratteri veri e diventano invisibili nel sorgente.
 */
function senzaControlli(v: string, tieni: string): string {
  let out = "";
  for (const ch of v) {
    const c = ch.codePointAt(0) ?? 0;
    if (c < 32 || c === 127) {
      if (tieni.includes(ch)) out += ch;
      continue;
    }
    out += ch;
  }
  return out;
}

/** Campi brevi: gli a capo e le sequenze di spazi diventano uno spazio
 *  solo. Tabulazione e a capo sopravvivono al filtro proprio per
 *  diventare quello spazio: toglierli del tutto incollerebbe fra loro le
 *  parole che separavano. */
function unaRiga(v: unknown): string {
  if (typeof v !== "string") return "";
  return senzaControlli(v, TABULAZIONE + A_CAPO)
    .replace(/\s+/g, " ")
    .trim();
}

/** Il messaggio invece conserva i paragrafi: normalizza i fine riga di
 *  Windows, toglie gli spazi in coda a ogni riga e riduce a due gli a
 *  capo consecutivi. Se lo si passasse da `unaRiga` arriverebbe a
 *  destinazione un muro di testo. */
function piuRighe(v: unknown): string {
  if (typeof v !== "string") return "";
  return senzaControlli(v.replace(/\r\n?/g, A_CAPO), TABULAZIONE + A_CAPO)
    .split(A_CAPO)
    .map((riga) => riga.replace(/[ \t]+$/, ""))
    .join(A_CAPO)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Controllo di forma, non di esistenza. Una regex non sa dire se una
 *  casella riceve posta, e le regex che ci provano rifiutano indirizzi
 *  legittimi: qui si chiede solo che ci sia una chiocciola con qualcosa
 *  intorno e un punto nel dominio. Chi scrive l'indirizzo sbagliato lo
 *  scopre quando non arriva la risposta, ed è un caso che nessuna
 *  validazione sintattica può coprire. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Toglie dalla partita IVA tutto quello che serve a leggerla e non a
 *  identificarla: spazi, punti, trattini e il prefisso `IT`, che metà
 *  delle imprese scrive e metà no. Quello che resta è il numero. */
function normalizzaPartitaIva(v: string): string {
  const compatta = v.toUpperCase().replace(/[\s.\-/]/g, "");
  return compatta.startsWith("IT") ? compatta.slice(2) : compatta;
}

/**
 * Cifra di controllo della partita IVA italiana (algoritmo dell'allegato
 * al d.m. 23 dicembre 1976): le cifre di posto pari si raddoppiano e,
 * se superano il nove, si sottrae nove; la somma di tutte e undici deve
 * essere un multiplo di dieci.
 *
 * Vale la pena farlo invece di contare solo le cifre: undici numeri
 * copiati a mano da una visura sono il campo del modulo in cui si sbaglia
 * più spesso, e un errore lì non si scopre mai leggendo il messaggio, si
 * scopre quando qualcuno cerca l'impresa e non la trova.
 */
function partitaIvaItalianaValida(n: string): boolean {
  if (!/^\d{11}$/.test(n)) return false;
  let somma = 0;
  for (let i = 0; i < 11; i++) {
    const cifra = n.charCodeAt(i) - 48;
    if (i % 2 === 0) {
      somma += cifra;
    } else {
      const doppia = cifra * 2;
      somma += doppia > 9 ? doppia - 9 : doppia;
    }
  }
  return somma % 10 === 0;
}

export function validaContatto(input: unknown): EsitoValidazione {
  const errori: ErroriContatto = {};
  const grezzo = (input ?? {}) as Record<string, unknown>;

  // Quale modulo sta scrivendo. Il ripiego è `contatti`, che è il modulo
  // più vecchio: una richiesta senza il campo arriva da una pagina
  // servita prima di questa modifica, e deve continuare a passare.
  const moduloGrezzo = unaRiga(grezzo.modulo);
  const modulo: Modulo = (MODULI as readonly string[]).includes(moduloGrezzo)
    ? (moduloGrezzo as Modulo)
    : "contatti";
  const briefing = modulo === "accoglienza";

  // I campi che il modulo di partenza non chiede non si leggono affatto.
  // Non è pignoleria: accettarli vorrebbe dire che una richiesta di
  // /accoglienza può portare interessi mai selezionati da nessuno, e a
  // valle nessuno saprebbe da dove sono usciti.
  const tipoGrezzo = briefing ? "" : unaRiga(grezzo.tipo);
  let tipo: TipoOrganizzazione | null = null;
  if (tipoGrezzo) {
    if (TIPI_VALIDI.includes(tipoGrezzo)) {
      tipo = tipoGrezzo as TipoOrganizzazione;
    } else {
      errori.tipo = "Profilo non riconosciuto.";
    }
  }

  const nome = unaRiga(grezzo.nome);
  if (!nome) {
    errori.nome = "Serve un nome per sapere chi richiamare.";
  } else if (nome.length < LIMITI.nome.min) {
    errori.nome = "Il nome è troppo corto.";
  } else if (nome.length > LIMITI.nome.max) {
    errori.nome = `Il nome supera i ${LIMITI.nome.max} caratteri.`;
  }

  const ruolo = briefing ? unaRiga(grezzo.ruolo) : "";
  if (ruolo.length > LIMITI.ruolo.max) {
    errori.ruolo = `Il ruolo supera i ${LIMITI.ruolo.max} caratteri.`;
  }

  const email = unaRiga(grezzo.email).toLowerCase();
  if (!email) {
    errori.email = "Serve un indirizzo a cui rispondere.";
  } else if (email.length > LIMITI.email.max) {
    errori.email = "L'indirizzo è troppo lungo.";
  } else if (!EMAIL.test(email)) {
    errori.email = "L'indirizzo non sembra valido: controlla la chiocciola e il dominio.";
  }

  const azienda = unaRiga(grezzo.azienda);
  if (briefing && !azienda) {
    errori.azienda = "Serve la ragione sociale dell'impresa.";
  } else if (azienda.length > LIMITI.azienda.max) {
    errori.azienda = `Il nome dell'ente supera i ${LIMITI.azienda.max} caratteri.`;
  }

  // Partita IVA: obbligatoria sul briefing, assente dall'altro modulo.
  // Il controllo è in due gradini perché le imprese che scrivono non
  // sono tutte italiane: undici cifre passano dalla cifra di controllo,
  // qualunque altra forma si accontenta di essere una sigla plausibile.
  // Rifiutare un numero estero valido, per essere severi con quelli
  // italiani, chiuderebbe fuori proprio chi ci scrive dall'estero.
  const partitaIvaGrezza = briefing ? unaRiga(grezzo.partitaIva) : "";
  let partitaIva = "";
  if (briefing) {
    const n = normalizzaPartitaIva(partitaIvaGrezza);
    if (!partitaIvaGrezza) {
      errori.partitaIva = "Serve la partita IVA: è così che troviamo l'impresa.";
    } else if (partitaIvaGrezza.length > LIMITI.partitaIva.max) {
      errori.partitaIva = "La partita IVA è troppo lunga.";
    } else if (/^\d+$/.test(n)) {
      if (n.length !== 11) {
        errori.partitaIva = "La partita IVA italiana ha undici cifre.";
      } else if (!partitaIvaItalianaValida(n)) {
        errori.partitaIva = "Il numero non risulta valido: ricontrolla le cifre.";
      } else {
        partitaIva = n;
      }
    } else if (!/^[A-Z0-9]{8,14}$/.test(n)) {
      errori.partitaIva = "Il numero non sembra valido: ricontrollalo.";
    } else {
      partitaIva = n;
    }
  }

  const settore = briefing ? unaRiga(grezzo.settore) : "";
  if (settore.length > LIMITI.settore.max) {
    errori.settore = `Il settore supera i ${LIMITI.settore.max} caratteri.`;
  }

  // Telefono facoltativo. Non si pretende un formato: prefissi, interni e
  // spaziature cambiano da paese a paese e da centralino a centralino, e
  // una regex stretta qui rifiuta numeri veri. Si chiede solo che ci
  // siano abbastanza cifre da poter essere un numero.
  const telefono = briefing ? unaRiga(grezzo.telefono) : "";
  if (telefono) {
    const cifre = telefono.replace(/\D/g, "");
    if (telefono.length > LIMITI.telefono.max) {
      errori.telefono = "Il numero è troppo lungo.";
    } else if (!/^[+\d][\d\s().\-/]*$/.test(telefono) || cifre.length < 6) {
      errori.telefono = "Il numero non sembra valido: ricontrollalo.";
    }
  }

  // Sito web: facoltativo, su /contatti e (dal 25/10/2026) sul briefing.
  // Chi lo scrive quasi mai mette lo schema («impresa.it»,
  // «www.impresa.it»), e rifiutarlo per questo sarebbe pignoleria: lo si aggiunge e poi si chiede a `URL` se
  // ne esce un indirizzo web vero. Fuori restano gli schemi che non sono
  // web (`javascript:`, `mailto:`), le credenziali dentro l'indirizzo
  // (`utente:password@`, che a valle finirebbero in chiaro in una casella
  // di posta) e i nomi senza un punto, che non sono un dominio.
  const sitoGrezzo = unaRiga(grezzo.sito);
  let sito = "";
  if (sitoGrezzo) {
    if (sitoGrezzo.length > LIMITI.sito.max) {
      errori.sito = `L'indirizzo del sito supera i ${LIMITI.sito.max} caratteri.`;
    } else {
      // Lo schema si aggiunge solo se manca davvero: con `://` c'è già, e
      // gli schemi senza barre si lasciano com'erano perché `URL` li
      // riconosca e il controllo li rifiuti. Un `:` qualunque non basta a
      // dire che uno schema c'è: «www.impresa.it:8080» non ne ha.
      const conSchema =
        sitoGrezzo.includes("://") || /^(javascript|data|vbscript|mailto|tel|file):/i.test(sitoGrezzo)
          ? sitoGrezzo
          : `https://${sitoGrezzo}`;
      let indirizzo: URL | null = null;
      try {
        indirizzo = new URL(conSchema);
      } catch {
        indirizzo = null;
      }
      if (
        !indirizzo ||
        (indirizzo.protocol !== "https:" && indirizzo.protocol !== "http:") ||
        indirizzo.username ||
        indirizzo.password ||
        !indirizzo.hostname.includes(".") ||
        indirizzo.hostname.endsWith(".")
      ) {
        errori.sito = "L'indirizzo del sito non sembra valido: per esempio www.impresa.it.";
      } else {
        // `href` aggiunge la barra finale a un dominio nudo: la si toglie,
        // così «impresa.it» arriva come https://impresa.it e non con una
        // barra che chi l'ha scritto non ha mai messo.
        sito =
          indirizzo.pathname === "/" && !indirizzo.search && !indirizzo.hash
            ? `${indirizzo.protocol}//${indirizzo.host}`
            : indirizzo.href;
      }
    }
  }

  /** Lista di chiavi da un vocabolario chiuso: stessa forma per i servizi
   *  di /contatti e per gli obiettivi del briefing. Una chiave inventata
   *  si rifiuta, perché a valle diventa un riferimento a qualcosa che non
   *  esiste; i doppioni si tolgono in silenzio, perché non sono un
   *  errore di chi compila ma di chi ha mandato la richiesta due volte. */
  function listaChiusa(
    valore: unknown,
    validi: readonly string[],
    massimo: number,
    messaggi: { forma: string; ignota: string; troppi: string },
    campo: "interessi" | "obiettivi"
  ): string[] {
    if (valore === undefined || valore === null) return [];
    if (!Array.isArray(valore)) {
      errori[campo] = messaggi.forma;
      return [];
    }
    const puliti = valore.map((v) => unaRiga(v)).filter(Boolean);
    if (puliti.some((s) => !validi.includes(s))) {
      errori[campo] = messaggi.ignota;
      return [];
    }
    if (puliti.length > massimo) {
      errori[campo] = messaggi.troppi;
      return [];
    }
    return [...new Set(puliti)];
  }

  // Gli interessi sono facoltativi: chi scrive può non sapere ancora
  // quale servizio gli serve, ed è metà del motivo per cui scrive.
  const interessi = briefing
    ? []
    : listaChiusa(
        grezzo.interessi,
        SLUG_VALIDI,
        LIMITI.interessi.max,
        {
          forma: "Elenco dei servizi non valido.",
          ignota: "Uno dei servizi selezionati non esiste.",
          troppi: "Troppi servizi selezionati.",
        },
        "interessi"
      );

  const obiettivi = briefing
    ? listaChiusa(
        grezzo.obiettivi,
        OBIETTIVI_VALIDI,
        LIMITI.obiettivi.max,
        {
          forma: "Elenco degli obiettivi non valido.",
          ignota: "Uno degli obiettivi selezionati non esiste.",
          troppi: "Troppi obiettivi selezionati.",
        },
        "obiettivi"
      )
    : [];

  const messaggio = piuRighe(grezzo.messaggio);
  if (!messaggio) {
    errori.messaggio =
      "Scrivi due righe sull'esigenza: senza, non sappiamo chi farti richiamare.";
  } else if (messaggio.length < LIMITI.messaggio.min) {
    errori.messaggio = `Serve qualche parola in più: almeno ${LIMITI.messaggio.min} caratteri.`;
  } else if (messaggio.length > LIMITI.messaggio.max) {
    errori.messaggio = `Il messaggio supera i ${LIMITI.messaggio.max} caratteri: sintetizza, il resto si dice a voce.`;
  }

  // Presa visione dell'informativa. Il confronto è con `true` e non con
  // un valore di verità qualunque: una casella non spuntata arriva come
  // `false`, ma un client sciatto potrebbe mandare la stringa "false",
  // che in JavaScript è vera. Qui una dichiarazione vale solo se è stata
  // fatta per davvero.
  //
  // Attenzione a che cosa è e a che cosa non è. Non è il consenso al
  // trattamento: la base giuridica della risposta a chi scrive è
  // l'esecuzione di misure precontrattuali (art. 6, par. 1, lett. b), e
  // chiedere un consenso dove la legge non lo richiede è scorretto due
  // volte, perché lascia credere che il trattamento si possa fermare
  // revocandolo. È la prova che l'informativa dell'art. 13 è stata resa
  // **prima** della raccolta, che è l'obbligo vero.
  if (grezzo.presaVisione !== true) {
    errori.presaVisione =
      "Per inviare la richiesta conferma di aver letto l'informativa privacy.";
  }

  if (Object.keys(errori).length) return { ok: false, errori };

  return {
    ok: true,
    dati: {
      modulo,
      tipo,
      nome,
      ruolo: ruolo || null,
      email,
      azienda: azienda || null,
      partitaIva: partitaIva || null,
      settore: settore || null,
      telefono: telefono || null,
      sito: sito || null,
      interessi,
      obiettivi,
      messaggio,
      informativaVersione: INFORMATIVA_VERSIONE,
      consensoAggiornamenti: grezzo.consensoAggiornamenti === true,
    },
  };
}

/** L'ordine di lettura, per annunciare in cima **un** errore, il primo:
 *  elencarli tutti due volte, in testa e sotto i campi, raddoppia il
 *  rumore senza aggiungere informazione.
 *
 *  È un elenco solo per tutti e due i moduli e non due elenchi da
 *  scegliere: ogni modulo usa il suo sottoinsieme, e l'ordine relativo
 *  dentro ciascuno resta quello con cui la pagina si legge. */
export const ORDINE_CAMPI: CampoContatto[] = [
  // Su /contatti i servizi sono il passaggio 01, cioè la prima cosa che
  // si legge (modello del 19/10/2026): stanno in testa. Il briefing non
  // li ha, quindi per lui l'ordine non cambia.
  "interessi",
  "tipo",
  "nome",
  "ruolo",
  "email",
  "azienda",
  "sito",
  "partitaIva",
  "settore",
  "telefono",
  "obiettivi",
  "messaggio",
  "presaVisione",
];

export function primoErrore(errori: ErroriContatto): string | null {
  for (const campo of ORDINE_CAMPI) {
    const e = errori[campo];
    if (e) return e;
  }
  return null;
}
